/**
 * Spanish dictionary. Keys are the English source strings; `{placeholders}` must be kept verbatim.
 * Register: formal ("usted"), as is customary for B2B software. Completeness is enforced by tests/i18n.test.ts.
 */
export const es: Readonly<Record<string, string>> = {
  // Placeholders & units
  '— not available for this base': '— no disponible para esta base',
  '{ext} is not allowed': 'No se permite {ext}',
  '{n} d': '{n} d',
  '{n} days': '{n} días',
  '{n} h': '{n} h',
  '{n} recipient(s)': '{n} destinatario(s)',
  '{name} logo': 'Logotipo de {name}',
  '{resolved} resolved of {created} created': '{resolved} resueltos de {created} creados',
  '12 months': '12 meses',
  '< 1 day': '< 1 día',
  '1–3 days': '1–3 días',
  '3–7 days': '3–7 días',
  '7–30 days': '7–30 días',
  '> 30 days': '> 30 días',
  '90th percentile': 'Percentil 90',
  ResolveOne: 'ResolveOne',
  total: 'total',
  Total: 'Total',

  // Navigation & shell
  Administration: 'Administración',
  'API documentation ↗': 'Documentación de la API ↗',
  'Appearance & language': 'Apariencia e idioma',
  'Close menu': 'Cerrar menú',
  'Collapse menu': 'Contraer menú',
  Dashboard: 'Panel',
  'Expand menu': 'Expandir menú',
  Language: 'Idioma',
  'Main navigation': 'Navegación principal',
  'New ticket': 'Nuevo ticket',
  Notifications: 'Notificaciones',
  'Open menu': 'Abrir menú',
  Primary: 'Principal',
  Reports: 'Informes',
  'Restoring session…': 'Restaurando la sesión…',
  Settings: 'Configuración',
  'Sign out': 'Cerrar sesión',
  'Skip to content': 'Ir al contenido',
  Theme: 'Tema',
  'Theme: {mode}. Switch to {next}.': 'Tema: {mode}. Cambiar a {next}.',
  System: 'Sistema',
  Light: 'Claro',
  Dark: 'Oscuro',
  Tickets: 'Tickets',
  Breadcrumb: 'Ruta de navegación',
  Pagination: 'Paginación',

  // Generic actions & states
  Action: 'Acción',
  Actions: 'Acciones',
  Activate: 'Activar',
  Active: 'Activo',
  Assign: 'Asignar',
  Cancel: 'Cancelar',
  Changes: 'Cambios',
  Confirm: 'Confirmar',
  Customize: 'Personalizar',
  Deactivate: 'Desactivar',
  Deactivated: 'Desactivado',
  Delete: 'Eliminar',
  'Delete {name}': 'Eliminar {name}',
  Edit: 'Editar',
  Inactive: 'Inactivo',
  inactive: 'inactiva',
  'Load more': 'Cargar más',
  'Loading…': 'Cargando…',
  Manage: 'Gestionar',
  Name: 'Nombre',
  Never: 'Nunca',
  Next: 'Siguiente',
  'Nothing to show.': 'No hay nada que mostrar.',
  Pause: 'Pausar',
  Previous: 'Anterior',
  Reactivate: 'Reactivar',
  Reject: 'Rechazar',
  'Reject…': 'Rechazar…',
  Reload: 'Recargar',
  Remove: 'Quitar',
  'Remove {name}': 'Quitar {name}',
  Resume: 'Reanudar',
  Retry: 'Reintentar',
  Save: 'Guardar',
  'Save changes': 'Guardar cambios',
  'Saved.': 'Guardado.',
  'Saving…': 'Guardando…',
  'Select…': 'Seleccione…',
  'Sending…': 'Enviando…',
  'Something went wrong': 'Algo salió mal',
  'Something went wrong. Please try again.': 'Algo salió mal. Inténtelo de nuevo.',
  'Unable to reach the server. Check your connection and try again.': 'No se puede conectar con el servidor. Compruebe su conexión e inténtelo de nuevo.',
  Type: 'Tipo',
  When: 'Cuándo',
  Key: 'Clave',
  Order: 'Orden',
  Preview: 'Vista previa',

  // Auth & passwords
  'At least 12 characters': 'Al menos 12 caracteres',
  'At least 12 characters, using three of: lowercase, uppercase, digits, symbols.': 'Al menos 12 caracteres, con tres de: minúsculas, mayúsculas, dígitos y símbolos.',
  'Back to sign in': 'Volver a iniciar sesión',
  'Choose a new password': 'Elija una nueva contraseña',
  'Confirm password': 'Confirmar contraseña',
  Email: 'Correo electrónico',
  'Forgot your password?': '¿Olvidó su contraseña?',
  'If an account exists for that email, a reset link is on its way. It expires in 60 minutes.': 'Si existe una cuenta con ese correo, le enviaremos un enlace para restablecer la contraseña. Caduca en 60 minutos.',
  'Invalid link': 'Enlace no válido',
  'Looks good.': 'Correcto.',
  'Need help?': '¿Necesita ayuda?',
  'New password': 'Nueva contraseña',
  Password: 'Contraseña',
  'Password set. You can now sign in.': 'Contraseña establecida. Ya puede iniciar sesión.',
  'Passwords do not match': 'Las contraseñas no coinciden',
  'Request a new one': 'Solicite uno nuevo',
  'Reset your password': 'Restablecer la contraseña',
  'Send reset link': 'Enviar enlace',
  'Set password': 'Establecer contraseña',
  'Sign in': 'Iniciar sesión',
  'Sign in to {product}': 'Iniciar sesión en {product}',
  'Signing in…': 'Iniciando sesión…',
  'This link is incomplete.': 'Este enlace está incompleto.',
  'Three of: lowercase, uppercase, digits, symbols': 'Tres de: minúsculas, mayúsculas, dígitos y símbolos',
  'Welcome — set your password': 'Bienvenido: establezca su contraseña',
  Workspace: 'Espacio de trabajo',
  'Invalid credentials': 'Credenciales no válidas',
  'Account temporarily locked due to repeated failed logins': 'Cuenta bloqueada temporalmente por intentos fallidos repetidos',
  'Password does not meet the policy': 'La contraseña no cumple la política',
  'This link is invalid or has expired. Request a new one.': 'Este enlace no es válido o ha caducado. Solicite uno nuevo.',
  'This link has already been used.': 'Este enlace ya se ha utilizado.',

  // Tickets: list, filters, saved searches, export
  'All categories': 'Todas las categorías',
  'All priorities': 'Todas las prioridades',
  'All severities': 'Todas las severidades',
  'All statuses': 'Todos los estados',
  'Any assignee': 'Cualquier responsable',
  'Assigned to me': 'Asignados a mí',
  'Clear filters': 'Borrar filtros',
  Created: 'Creado',
  CSV: 'CSV',
  'Delete saved search {name}': 'Eliminar la búsqueda guardada {name}',
  'Excel (.xlsx)': 'Excel (.xlsx)',
  Export: 'Exportar',
  'Export…': 'Exportar…',
  'Exporting…': 'Exportando…',
  JSON: 'JSON',
  'No tickets match these filters.': 'Ningún ticket coincide con estos filtros.',
  PDF: 'PDF',
  'Pin to my dashboard': 'Anclar en mi panel',
  Pinned: 'Anclada',
  'Save search': 'Guardar búsqueda',
  'Save this search': 'Guardar esta búsqueda',
  'Saved searches': 'Búsquedas guardadas',
  'Saved searches…': 'Búsquedas guardadas…',
  'Search key, title, description, tags…': 'Buscar por clave, título, descripción, etiquetas…',
  'Search tickets': 'Buscar tickets',
  'Share with my team': 'Compartir con mi equipo',
  Shared: 'Compartida',
  'Specific person': 'Persona específica',
  'The export was capped at the maximum row count. Narrow the filters to export the rest.': 'La exportación se limitó al número máximo de filas. Acote los filtros para exportar el resto.',

  // Ticket form & detail
  'Actual behavior': 'Comportamiento real',
  'Add files': 'Añadir archivos',
  'Assign tickets': 'Asignar tickets',
  Assignee: 'Responsable',
  'At most {n} files per ticket at creation': 'Máximo {n} archivos por ticket al crearlo',
  'Attach files': 'Adjuntar archivos',
  Attachments: 'Adjuntos',
  'Back to tickets': 'Volver a los tickets',
  Browser: 'Navegador',
  'Business impact': 'Impacto en el negocio',
  Category: 'Categoría',
  'Change status': 'Cambiar estado',
  Classification: 'Clasificación',
  'Comma-separated': 'Separadas por comas',
  'Current category': 'Categoría actual',
  'Customer impact': 'Impacto en el cliente',
  Description: 'Descripción',
  Device: 'Dispositivo',
  Environment: 'Entorno',
  'e.g. production, staging': 'p. ej., producción, preproducción',
  'Expected behavior': 'Comportamiento esperado',
  'File is empty': 'El archivo está vacío',
  'Files to attach': 'Archivos para adjuntar',
  'Hide activity': 'Ocultar actividad',
  Impact: 'Impacto',
  'It may not exist, or you may not have access to it.': 'Puede que no exista o que no tenga acceso a él.',
  'Larger than {mb} MB': 'Supera {mb} MB',
  'Loading upload limits…': 'Cargando los límites de carga…',
  'No attachments.': 'No hay adjuntos.',
  'No category': 'Sin categoría',
  'Not attached': 'No adjuntado',
  'Operating system': 'Sistema operativo',
  OS: 'SO',
  'Product / module': 'Producto / módulo',
  'Reason for “{status}” (optional)': 'Motivo de «{status}» (opcional)',
  'Release / sprint': 'Entrega / sprint',
  Release: 'Versión de entrega',
  'Report a bug': 'Informar de un error',
  Reporter: 'Informante',
  Reproduction: 'Reproducción',
  'Save draft': 'Guardar borrador',
  'Screenshots, logs, documents or short videos help us reproduce the issue.': 'Las capturas, registros, documentos o vídeos cortos nos ayudan a reproducir el problema.',
  'Show activity': 'Mostrar actividad',
  'Steps to reproduce': 'Pasos para reproducir',
  'Submit ticket': 'Enviar ticket',
  'Submitting…': 'Enviando…',
  Summary: 'Resumen',
  Tags: 'Etiquetas',
  'The ticket was created, but some files could not be attached — add them again under Attachments:': 'El ticket se creó, pero algunos archivos no se pudieron adjuntar; vuelva a añadirlos en Adjuntos:',
  'This file type is not allowed': 'No se permite este tipo de archivo',
  'Ticket not found': 'Ticket no encontrado',
  Title: 'Título',
  'Up to {files} files per upload, {mb} MB each. Images, PDF, Office, text/log, ZIP and video.': 'Hasta {files} archivos por carga, {mb} MB cada uno. Imágenes, PDF, Office, texto/registros, ZIP y vídeo.',
  'Up to {n} files, {mb} MB each.': 'Hasta {n} archivos, {mb} MB cada uno.',
  'Uploading files… {pct}%': 'Subiendo archivos… {pct}%',
  'Uploading…': 'Subiendo…',
  'Uploading… {pct}%': 'Subiendo… {pct}%',
  Version: 'Versión',
  'Ticket was modified by another user; reload and retry': 'Otro usuario modificó el ticket; recargue e inténtelo de nuevo',
  'One or more files were rejected': 'Se rechazaron uno o más archivos',
  'Validation failed': 'La validación falló',
  'Insufficient permissions': 'Permisos insuficientes',
  'Category not found or inactive': 'La categoría no existe o está inactiva',
  'Choose an active category': 'Elija una categoría activa',

  // SLA
  Due: 'Vence',
  'First response': 'Primera respuesta',
  Met: 'Cumplido',
  Overdue: 'Vencido',
  'Service level': 'Nivel de servicio',
  'SLA breached': 'SLA incumplido',
  'SLA starts when the ticket is submitted.': 'El SLA comienza cuando se envía el ticket.',
  Resolution: 'Resolución',

  // Comments & timeline
  'Add a comment': 'Añadir un comentario',
  'Add a comment…': 'Añadir un comentario…',
  'added an internal note': 'añadió una nota interna',
  'changed status': 'cambió el estado',
  'changed status from {from} to {to}': 'cambió el estado de {from} a {to}',
  'changed the assignee': 'cambió el responsable',
  Comment: 'Comentar',
  commented: 'comentó',
  Comments: 'Comentarios',
  'created a draft': 'creó un borrador',
  Internal: 'Interno',
  'Internal note': 'Nota interna',
  'Internal only': 'Solo interno',
  'No activity yet.': 'Aún no hay actividad.',
  'No comments yet.': 'Aún no hay comentarios.',
  'Posting…': 'Publicando…',
  Reply: 'Responder',
  'submitted the ticket': 'envió el ticket',
  'This ticket is closed to new comments.': 'Este ticket no admite nuevos comentarios.',
  'updated {fields}': 'actualizó {fields}',
  'Write a reply…': 'Escriba una respuesta…',
  'Attachment added': 'Adjunto añadido',
  'Attachment removed': 'Adjunto eliminado',
  'Attachment rejected': 'Adjunto rechazado',
  'Sla warning': 'Aviso de SLA',
  'Sla breached': 'SLA incumplido',
  'Survey submitted': 'Encuesta enviada',
  'Unassigned on erasure': 'Desasignado por supresión',
  'Status changed': 'Estado cambiado',
  'Comment added': 'Comentario añadido',
  Updated: 'Actualizado',
  Reason: 'Motivo',

  // Satisfaction survey
  'Comments (optional)': 'Comentarios (opcional)',
  'Customer feedback': 'Opinión del cliente',
  Dissatisfied: 'Insatisfecho',
  'extremely likely': 'muy probable',
  'How did we do?': '¿Qué tal lo hicimos?',
  'How likely are you to recommend us? (optional)': '¿Qué probabilidad hay de que nos recomiende? (opcional)',
  'How satisfied are you with how this ticket was handled?': '¿Qué tan satisfecho está con la gestión de este ticket?',
  'Likelihood to recommend': 'Probabilidad de recomendarnos',
  Neutral: 'Neutral',
  'No answer': 'Sin respuesta',
  'not at all likely': 'nada probable',
  Satisfied: 'Satisfecho',
  'Send feedback': 'Enviar opinión',
  'Very dissatisfied': 'Muy insatisfecho',
  'Very satisfied': 'Muy satisfecho',
  'Your feedback': 'Su opinión',

  // Notifications & settings
  'Accent colour': 'Color de acento',
  'Daily digest': 'Resumen diario',
  'Every accent keeps button text readable (WCAG AA). Only you see your accent.': 'Todos los acentos mantienen legible el texto de los botones (WCAG AA). Solo usted ve su acento.',
  Immediately: 'Inmediatamente',
  'In-app': 'En la aplicación',
  'In-app notifications only': 'Solo notificaciones en la aplicación',
  'Mark all as read': 'Marcar todo como leído',
  'No notifications yet.': 'Aún no hay notificaciones.',
  'One email per update': 'Un correo por cada actualización',
  'One summary email every Monday': 'Un resumen por correo cada lunes',
  'One summary email per day': 'Un resumen por correo al día',
  'Show new notifications as unread in the notification bell': 'Mostrar las nuevas notificaciones como no leídas en la campana',
  'System follows your device setting.': 'Sistema sigue la configuración de su dispositivo.',
  Unread: 'No leída',
  'Unread only': 'Solo no leídas',
  '{label}, {n} unread': '{label}, {n} sin leer',
  'Weekly digest': 'Resumen semanal',
  'You are all caught up.': 'Está al día.',
  'Workspace brand': 'Marca del espacio',
  Blue: 'Azul',
  Teal: 'Verde azulado',
  Green: 'Verde',
  Violet: 'Violeta',
  Rose: 'Rosa',
  Orange: 'Naranja',

  // Privacy (GDPR)
  'A ZIP with everything we hold about you — profile, tickets, comments, files you uploaded and account activity — in machine-readable JSON. Links expire after 7 days.':
    'Un ZIP con todo lo que conservamos sobre usted (perfil, tickets, comentarios, archivos subidos y actividad de la cuenta) en JSON legible por máquina. Los enlaces caducan a los 7 días.',
  'Account erasure': 'Supresión de la cuenta',
  'As an administrator, this takes effect immediately and signs you out.': 'Como administrador, esto se aplica de inmediato y cierra su sesión.',
  'available until {date}': 'disponible hasta el {date}',
  'awaiting administrator review (due by {date})': 'pendiente de revisión del administrador (plazo: {date})',
  'Confirm account deletion': 'Confirmar la eliminación de la cuenta',
  'Data export': 'Exportación de datos',
  DELETE: 'ELIMINAR',
  'Delete my account…': 'Eliminar mi cuenta…',
  'Delete your account': 'Eliminar su cuenta',
  'Download ({size})': 'Descargar ({size})',
  'Download your data': 'Descargar sus datos',
  'Erasure requested': 'Supresión solicitada',
  'No requests yet.': 'Aún no hay solicitudes.',
  'Preparing export…': 'Preparando la exportación…',
  'Privacy & your data': 'Privacidad y sus datos',
  'Reason (optional)': 'Motivo (opcional)',
  // Workflow (customer stages, actions, guidance)
  'Change password': 'Cambiar contraseña',
  'Current password': 'Contraseña actual',
  'Changing…': 'Cambiando…',
  'Password changed. You’ve been signed out on your other devices.': 'Contraseña cambiada. Se cerró la sesión en sus otros dispositivos.',
  'Current password is incorrect': 'La contraseña actual no es correcta',
  'Choose a password different from the current one': 'Elija una contraseña distinta de la actual',
  'Use at least 12 characters': 'Use al menos 12 caracteres',
  'Use at most 256 characters': 'Use como máximo 256 caracteres',
  'Use at least three of: lowercase, uppercase, digits, symbols': 'Use al menos tres de: minúsculas, mayúsculas, dígitos y símbolos',
  'Must not contain your email name': 'No debe contener el nombre de su correo',
  'Session ended — please sign in again': 'La sesión terminó; inicie sesión de nuevo',
  '{key} was handed over and left your queue.': '{key} se entregó y salió de su cola.',
  'After handing it over, this ticket leaves your queue and you won’t be able to open it.': 'Al entregarlo, este ticket sale de su cola y ya no podrá abrirlo.',
  'Assign someone first (see Assignee) to: {actions}': 'Asigne primero a alguien (ver Responsable) para: {actions}',
  'Assign someone to this ticket first': 'Asigne primero a alguien a este ticket',
  'The assignee is no longer active — reassign the ticket first': 'El responsable ya no está activo: reasigne el ticket primero',
  'Tell the customer what information you need': 'Indique al cliente qué información necesita',
  'Say which ticket this duplicates': 'Indique de qué ticket es duplicado',
  'Ticket progress': 'Progreso del ticket',
  'completed': 'completado',
  'current step': 'paso actual',
  'What happens next': 'Qué sigue',
  'Reply now': 'Responder ahora',
  'More actions': 'Más acciones',
  'Go back': 'Volver',
  'replied, so the ticket went back to the team': 'respondió, así que el ticket volvió al equipo',
  'moved the ticket from {from} to {to}': 'movió el ticket de {from} a {to}',
  'Being worked on': 'En curso',
  'Waiting for you': 'Esperando su respuesta',
  'This draft is only visible to you. Submit it when it’s ready and the team will be notified.': 'Solo usted ve este borrador. Envíelo cuando esté listo y el equipo recibirá un aviso.',
  'Submitted': 'Enviado',
  'Back to assigned': 'Volver a asignado',
  'Start work': 'Empezar a trabajar',
  'Ask customer for info': 'Pedir información al cliente',
  'What do you need from the customer?': '¿Qué necesita del cliente?',
  'Send to QA': 'Enviar a QA',
  'Start testing': 'Empezar pruebas',
  'Pass QA and resolve': 'Aprobar QA y resolver',
  'Yes, it’s fixed — close ticket': 'Sí, está solucionado: cerrar ticket',
  'Reopen': 'Reabrir',
  'Why reopen? (optional)': '¿Por qué reabrir? (opcional)',
  'Put on hold': 'Poner en espera',
  'Why on hold? (optional)': '¿Por qué en espera? (opcional)',
  'Mark as duplicate': 'Marcar como duplicado',
  'Duplicate of which ticket?': '¿Duplicado de qué ticket?',
  'Cancel ticket': 'Cancelar ticket',
  'Why are you cancelling? (optional)': '¿Por qué lo cancela? (opcional)',
  'The team will stop working on this ticket.': 'El equipo dejará de trabajar en este ticket.',
  'Defer': 'Aplazar',
  'Why defer? (optional)': '¿Por qué aplazar? (opcional)',
  'Escalate': 'Escalar',
  'Why escalate? (optional)': '¿Por qué escalar? (opcional)',
  'We’ve received your ticket. The support team will review it shortly — no action is needed from you.': 'Hemos recibido su ticket. El equipo de soporte lo revisará en breve; no necesita hacer nada.',
  'Return to queue': 'Devolver a la cola',
  'Our team is working on this. No action is needed from you; we’ll notify you of any update.': 'Nuestro equipo está trabajando en esto. No necesita hacer nada; le avisaremos de cualquier novedad.',
  'We need more information from you. Reply in the comments below — your reply sends the ticket straight back to the team.': 'Necesitamos más información. Responda en los comentarios de abajo: su respuesta devuelve el ticket directamente al equipo.',
  'Back to development': 'Volver a desarrollo',
  'Fail QA and send back': 'Rechazar en QA y devolver',
  'What failed? (optional)': '¿Qué falló? (opcional)',
  'We believe this is fixed. Please check and confirm, or tell us what is still not working.': 'Creemos que está solucionado. Compruébelo y confírmelo, o díganos qué sigue sin funcionar.',
  'No, it’s still not fixed': 'No, sigue sin funcionar',
  'What is still not working?': '¿Qué sigue sin funcionar?',
  'This ticket is closed. If the problem comes back, create a new ticket.': 'Este ticket está cerrado. Si el problema vuelve, cree un ticket nuevo.',
  'Reopen ticket': 'Reabrir ticket',
  'Resume work': 'Reanudar trabajo',
  'Closed as duplicate': 'Cerrado como duplicado',
  'Draft — not yet submitted.': 'Borrador: aún no enviado.',
  'QA': 'QA',
  'Close ticket': 'Cerrar ticket',
  'Triage it: assign someone, ask the customer for details, or mark it as a duplicate.': 'Clasifíquelo: asigne a alguien, pida detalles al cliente o márquelo como duplicado.',
  'Assigned. Start work when you pick it up.': 'Asignado. Empiece a trabajar cuando lo tome.',
  'Send it to QA when the fix is ready, or ask the customer if you’re blocked.': 'Envíelo a QA cuando la corrección esté lista, o pregunte al cliente si está bloqueado.',
  'Waiting for the customer (SLA paused). It returns to the team automatically when they reply.': 'Esperando al cliente (SLA en pausa). Vuelve al equipo automáticamente cuando responda.',
  'Waiting on customer': 'Esperando al cliente',
  'Resume without reply': 'Reanudar sin respuesta',
  'Waiting for QA to start testing.': 'Esperando a que QA empiece las pruebas.',
  'Ready for QA': 'Listo para QA',
  'Pass QA to resolve it, or fail it to send it back to development.': 'Apruébelo en QA para resolverlo, o recházelo para devolverlo a desarrollo.',
  'Waiting for the customer to confirm the fix.': 'Esperando a que el cliente confirme la corrección.',
  'Closed. Reopen it if the problem returns.': 'Cerrado. Reábralo si el problema vuelve.',
  'Reopened — assign it or start work again.': 'Reabierto: asígnelo o vuelva a trabajar en él.',
  'On hold (SLA paused). Resume work when unblocked.': 'En espera (SLA en pausa). Reanude el trabajo cuando se desbloquee.',
  'Closed as a duplicate.': 'Cerrado como duplicado.',
  'Cancelled — no further work.': 'Cancelado: sin más trabajo.',
  'Deferred (SLA paused). Return it to the queue or assign it when it’s scheduled.': 'Aplazado (SLA en pausa). Devuélvalo a la cola o asígnelo cuando esté planificado.',
  'Escalated to the team lead for attention.': 'Escalado al jefe de equipo.',
  'Tell us what is still not working': 'Díganos qué sigue sin funcionar',
  'Remember me': 'Recordarme',
  'Not you? Use another account': '¿No es usted? Use otra cuenta',
  'Keeps you signed in on this device for {n} days and remembers your email. Don’t use on shared computers.': 'Mantiene la sesión iniciada en este dispositivo durante {n} días y recuerda su correo. No lo use en equipos compartidos.',
  URL: 'URL',
  Other: 'Otro',
  'Not specified': 'Sin especificar',
  'Priority: {priority}': 'Prioridad: {priority}',
  'e.g. checkout, mobile': 'p. ej., pago, móvil',
  'Must start with http:// or https://': 'Debe empezar por http:// o https://',
  '(optional)': '(opcional)',
  'Request deletion': 'Solicitar eliminación',
  'Request my data': 'Solicitar mis datos',
  'Requested {date}': 'Solicitado el {date}',
  'Type {word} to confirm': 'Escriba {word} para confirmar',
  'Your account is closed and your personal details erased. Ticket records are kept for the service history without your identity, and what you wrote in them is redacted. An administrator reviews the request within one month.':
    'Su cuenta se cierra y sus datos personales se suprimen. Los tickets se conservan en el historial del servicio sin su identidad, y lo que escribió en ellos se oculta. Un administrador revisa la solicitud en un plazo de un mes.',
  'Your account is closed and your personal details erased. Ticket records are kept for the service history without your identity.':
    'Su cuenta se cierra y sus datos personales se suprimen. Los tickets se conservan en el historial del servicio sin su identidad.',
  'Your requests': 'Sus solicitudes',

  // Dashboard
  'At risk (≥75% SLA used)': 'En riesgo (≥75 % del SLA)',
  'Avg resolution time': 'Tiempo medio de resolución',
  'Created vs resolved': 'Creados frente a resueltos',
  'Customer satisfaction': 'Satisfacción del cliente',
  'Key figures': 'Cifras clave',
  'No surveys yet': 'Aún no hay encuestas',
  'Nothing open.': 'No hay nada abierto.',
  'NPS {nps} · {n} surveys': 'NPS {nps} · {n} encuestas',
  'Open by priority': 'Abiertos por prioridad',
  'Open by status': 'Abiertos por estado',
  'Open ticket age': 'Antigüedad de tickets abiertos',
  'Open tickets': 'Tickets abiertos',
  'Open tickets by age': 'Tickets abiertos por antigüedad',
  'Open tickets by priority': 'Tickets abiertos por prioridad',
  'Per day, last {n} days (UTC)': 'Por día, últimos {n} días (UTC)',
  'Per week, last {n} days (UTC)': 'Por semana, últimos {n} días (UTC)',
  Period: 'Periodo',
  'Pin a saved search from the Tickets page to track it here.': 'Ancle una búsqueda guardada desde Tickets para seguirla aquí.',
  'Pinned searches': 'Búsquedas ancladas',
  'Resolution rate': 'Tasa de resolución',
  'Show on my dashboard': 'Mostrar en mi panel',
  'SLA breached, open': 'SLA incumplido, abiertos',
  'Tickets created versus resolved': 'Tickets creados frente a resueltos',
  'Show data': 'Mostrar datos',
  'chart data': 'datos del gráfico',
  Legend: 'Leyenda',
  'Use arrow keys to inspect values.': 'Use las flechas para consultar los valores.',

  // Reports (client and server-provided metadata)
  Age: 'Antigüedad',
  Assigned: 'Asignado',
  'Automatic (SLA)': 'Automática (SLA)',
  Average: 'Media',
  'Avg resolution': 'Resolución media',
  'Backlog analysis': 'Análisis de pendientes',
  Breached: 'Incumplidos',
  Compliance: 'Cumplimiento',
  'Create schedule': 'Crear programación',
  CSAT: 'CSAT',
  'CSAT (1–5)': 'CSAT (1–5)',
  'CSAT and NPS by product.': 'CSAT y NPS por producto.',
  'CSAT and NPS by the assignee at the time of the survey.': 'CSAT y NPS por el responsable en el momento de la encuesta.',
  Day: 'Día',
  'Day of month': 'Día del mes',
  'Each run covers the previous {n} days. Only people allowed to view this report are listed.': 'Cada envío cubre los {n} días anteriores. Solo aparecen las personas que pueden ver este informe.',
  'Escalation analysis': 'Análisis de escalados',
  Escalations: 'Escalados',
  'Export {format}': 'Exportar {format}',
  'Filter by product (exact)': 'Filtrar por producto (exacto)',
  'First response met': 'Primera respuesta cumplida',
  Format: 'Formato',
  Frequency: 'Frecuencia',
  'Generated {date} · periods in UTC': 'Generado el {date} · periodos en UTC',
  'Hour (UTC)': 'Hora (UTC)',
  Manual: 'Manual',
  Median: 'Mediana',
  'New schedule': 'Nueva programación',
  'Next: {date}': 'Siguiente: {date}',
  'No data for this period.': 'No hay datos para este periodo.',
  'No scheduled reports yet.': 'Aún no hay informes programados.',
  NPS: 'NPS',
  'on {day}': 'el {day}',
  'on day {n}': 'el día {n}',
  'at {hour}:00 UTC': 'a las {hour}:00 UTC',
  'Open backlog and tickets created, resolved and closed in the period, by category.': 'Pendientes abiertos y tickets creados, resueltos y cerrados en el periodo, por categoría.',
  'Open backlog and tickets created, resolved and closed in the period, by priority.': 'Pendientes abiertos y tickets creados, resueltos y cerrados en el periodo, por prioridad.',
  'Open backlog and tickets created, resolved and closed in the period, by product.': 'Pendientes abiertos y tickets creados, resueltos y cerrados en el periodo, por producto.',
  'Open now': 'Abiertos ahora',
  'Open tickets now, by age since submission and priority.': 'Tickets abiertos ahora, por antigüedad desde el envío y prioridad.',
  'Per assignee: current open load, resolutions, speed, SLA breaches, reopens and satisfaction in the period.':
    'Por responsable: carga abierta actual, resoluciones, rapidez, incumplimientos de SLA, reaperturas y satisfacción en el periodo.',
  Product: 'Producto',
  Recipients: 'Destinatarios',
  'Reopen rate': 'Tasa de reapertura',
  'Reopened ticket trend': 'Tendencia de tickets reabiertos',
  Report: 'Informe',
  'Report options': 'Opciones del informe',
  'Resolution time': 'Tiempo de resolución',
  Responses: 'Respuestas',
  'Satisfaction by product': 'Satisfacción por producto',
  'Satisfaction by team member': 'Satisfacción por miembro del equipo',
  'Satisfaction trend': 'Tendencia de satisfacción',
  'Schedule “{title}”': 'Programar «{title}»',
  'Scheduled delivery': 'Envío programado',
  'SLA breaches': 'Incumplimientos de SLA',
  'SLA compliance': 'Cumplimiento del SLA',
  Surveys: 'Encuestas',
  'Team member': 'Miembro del equipo',
  'Team performance': 'Rendimiento del equipo',
  'Tickets by category': 'Tickets por categoría',
  'Tickets by priority': 'Tickets por prioridad',
  'Tickets by product': 'Tickets por producto',
  'Tickets resolved in the period and the share resolved within the resolution SLA; first-response attainment.':
    'Tickets resueltos en el periodo y la proporción resuelta dentro del SLA; cumplimiento de la primera respuesta.',
  'Time from submission to resolution for tickets resolved in the period.': 'Tiempo desde el envío hasta la resolución de los tickets resueltos en el periodo.',
  'Week of': 'Semana del',
  'Weekly CSAT and NPS from post-resolution surveys.': 'CSAT y NPS semanales de las encuestas posteriores a la resolución.',
  'Weekly escalations, split between automatic (SLA breach) and manual.': 'Escalados semanales, divididos entre automáticos (incumplimiento de SLA) y manuales.',
  'Weekly resolutions versus reopens, with the reopen rate.': 'Resoluciones semanales frente a reaperturas, con la tasa de reapertura.',
  'Within SLA': 'Dentro del SLA',
  Sunday: 'domingo',
  Monday: 'lunes',
  Tuesday: 'martes',
  Wednesday: 'miércoles',
  Thursday: 'jueves',
  Friday: 'viernes',
  Saturday: 'sábado',
  Excel: 'Excel',

  // Administration
  'Add a category': 'Añadir una categoría',
  'Add category': 'Añadir categoría',
  'Add organization': 'Añadir organización',
  'All events': 'Todos los eventos',
  'All requests': 'Todas las solicitudes',
  'All roles': 'Todos los roles',
  'Approve erasure': 'Aprobar supresión',
  'Audit log': 'Registro de auditoría',
  'Automatically escalate tickets when the resolution SLA is breached': 'Escalar automáticamente los tickets cuando se incumpla el SLA de resolución',
  'Based on': 'Basado en',
  Branding: 'Marca',
  'Built-in': 'Integrado',
  Categories: 'Categorías',
  'Confirm erasure (irreversible)': 'Confirmar supresión (irreversible)',
  'Contrast with white text is {ratio}:1; at least 4.5:1 is required. Choose a darker shade.': 'El contraste con texto blanco es {ratio}:1; se requiere al menos 4,5:1. Elija un tono más oscuro.',
  'Contrast with white text: {ratio}:1 — meets WCAG AA.': 'Contraste con texto blanco: {ratio}:1; cumple WCAG AA.',
  'Create role': 'Crear rol',
  Custom: 'Personalizado',
  'Custom role': 'Rol personalizado',
  'Custom roles start from a built-in role and adjust its permissions. The base role keeps deciding what data people see; staff-only and administrator-only powers can\'t be granted to customer-based roles.':
    'Los roles personalizados parten de un rol integrado y ajustan sus permisos. El rol base sigue decidiendo qué datos ve cada persona; las facultades exclusivas del personal y de los administradores no pueden otorgarse a roles basados en clientes.',
  'Data-subject requests under GDPR. Respond within one month of receipt (Art. 12(3)). Approving an erasure is irreversible.':
    'Solicitudes de interesados según el RGPD. Responda en el plazo de un mes desde la recepción (art. 12.3). Aprobar una supresión es irreversible.',
  'Decides what data the role can see and its ticket workflow. Fixed once created.': 'Determina qué datos ve el rol y su flujo de tickets. No se puede cambiar tras crearlo.',
  'Edit “{name}”': 'Editar «{name}»',
  'Edit category {name}': 'Editar la categoría {name}',
  'Edit role': 'Editar rol',
  Entity: 'Entidad',
  'Entity type': 'Tipo de entidad',
  'Erase…': 'Suprimir…',
  Erasure: 'Supresión',
  'Erasure (redact content)': 'Supresión (ocultar contenido)',
  'Erasure started.': 'Supresión iniciada.',
  'Export data': 'Exportar datos',
  'Export requested — you will be emailed when it is ready.': 'Exportación solicitada: recibirá un correo cuando esté lista.',
  'First response (h)': 'Primera respuesta (h)',
  'Invitation sent to {email}.': 'Invitación enviada a {email}.',
  'Invite a user': 'Invitar a un usuario',
  'Invite sent': 'Invitación enviada',
  'Inviting…': 'Invitando…',
  IP: 'IP',
  'Last sign-in': 'Último acceso',
  Logo: 'Logotipo',
  'New category': 'Nueva categoría',
  'New custom role': 'Nuevo rol personalizado',
  'New role': 'Nuevo rol',
  'No audit events.': 'No hay eventos de auditoría.',
  'No categories yet. Tickets can still be filed without one.': 'Aún no hay categorías. Los tickets pueden registrarse sin una.',
  'No logo': 'Sin logotipo',
  'No organizations yet.': 'Aún no hay organizaciones.',
  'No requests.': 'No hay solicitudes.',
  'No users match.': 'Ningún usuario coincide.',
  'Not available for {base}-based roles': 'No disponible para roles basados en {base}',
  Organization: 'Organización',
  Organizations: 'Organizaciones',
  'Overdue · {date}': 'Vencido · {date}',
  'Page {page} of {total} · {count} users': 'Página {page} de {total} · {count} usuarios',
  Permissions: 'Permisos',
  'Permissions of {name}': 'Permisos de {name}',
  Person: 'Persona',
  'Pick primary colour': 'Elegir color principal',
  'PNG, JPEG or WebP, up to 512 KB. SVG is not accepted.': 'PNG, JPEG o WebP, hasta 512 KB. No se admite SVG.',
  'Primary action': 'Acción principal',
  'Primary colour': 'Color principal',
  'Privacy requests': 'Solicitudes de privacidad',
  'Product name': 'Nombre del producto',
  'Reason (sent to the person)': 'Motivo (se envía a la persona)',
  'Reason for rejection': 'Motivo del rechazo',
  'Reassign its users first': 'Reasigne primero a sus usuarios',
  Received: 'Recibida',
  'Resend invite': 'Reenviar invitación',
  'Reset to {base} defaults': 'Restablecer valores de {base}',
  'Resolution (h)': 'Resolución (h)',
  Role: 'Rol',
  'Role for {name}': 'Rol de {name}',
  Roles: 'Roles',
  'Save branding': 'Guardar marca',
  'Save policy': 'Guardar política',
  'Save role': 'Guardar rol',
  'Saved. Branding is live for everyone in this workspace.': 'Guardado. La marca ya está activa para todo el espacio de trabajo.',
  'Search name or email…': 'Buscar por nombre o correo…',
  'Search users': 'Buscar usuarios',
  'Send invitation': 'Enviar invitación',
  'Shown in the header, browser tab, emails and PDF exports.': 'Se muestra en el encabezado, la pestaña del navegador, los correos y las exportaciones PDF.',
  'Sign-in message': 'Mensaje de inicio de sesión',
  'SLA policy': 'Política de SLA',
  'Sort order': 'Orden',
  Status: 'Estado',
  'Support email': 'Correo de soporte',
  'Targets in hours. Changes apply to tickets submitted after saving; open tickets keep the SLA they were given.': 'Objetivos en horas. Los cambios se aplican a los tickets enviados después de guardar; los abiertos conservan su SLA.',
  Tier: 'Nivel',
  'Tier for {name}': 'Nivel de {name}',
  'Upload logo': 'Subir logotipo',
  'Use a #RRGGBB hex colour.': 'Use un color hexadecimal #RRGGBB.',
  'Used by tickets — deactivate it instead': 'La usan tickets: desactívela en su lugar',
  User: 'Usuario',
  'User created, but the invitation email failed. Use "Resend invite".': 'Usuario creado, pero falló el correo de invitación. Use «Reenviar invitación».',
  Users: 'Usuarios',
  Ticket: 'Ticket',
  Tenant: 'Espacio',
  Auth: 'Autenticación',
  Builtin: 'Integrado',

  // Permission catalogue (server)
  Tickets_group: 'Tickets',
  Collaboration: 'Colaboración',
  Reporting: 'Informes',
  'Create tickets': 'Crear tickets',
  'View tickets': 'Ver tickets',
  'Edit ticket details': 'Editar detalles del ticket',
  'Change priority, severity, product and category': 'Cambiar prioridad, severidad, producto y categoría',
  'Delete tickets': 'Eliminar tickets',
  'Comment and attach files': 'Comentar y adjuntar archivos',
  'Internal notes and attachments': 'Notas y adjuntos internos',
  'View the audit log': 'Ver el registro de auditoría',
  'View users': 'Ver usuarios',
  'Invite and manage users': 'Invitar y gestionar usuarios',
  'View organizations': 'Ver organizaciones',
  'Manage organizations': 'Gestionar organizaciones',
  'Manage ticket categories': 'Gestionar categorías de tickets',
  'Workspace settings, SLA policy and branding': 'Configuración del espacio, política de SLA y marca',
  'Management reports': 'Informes de gestión',
  'Schedule report delivery': 'Programar el envío de informes',
  'Handle GDPR requests for others': 'Gestionar solicitudes RGPD de otras personas',
  'Manage roles': 'Gestionar roles',

  // Built-in role descriptions (server)
  'Reports and follows their own tickets.': 'Registra y sigue sus propios tickets.',
  'Sees all tickets of their organization and manages its users.': 'Ve todos los tickets de su organización y gestiona sus usuarios.',
  'Triages incoming tickets, assigns and communicates with customers.': 'Clasifica los tickets entrantes, los asigna y se comunica con los clientes.',
  'Works on assigned tickets and adds technical notes.': 'Trabaja en los tickets asignados y añade notas técnicas.',
  'Verifies fixes and reopens defects.': 'Verifica las correcciones y reabre defectos.',
  'Manages assignments, reports, SLAs and categories.': 'Gestiona asignaciones, informes, SLA y categorías.',
  'Full control of the workspace.': 'Control total del espacio de trabajo.',

  // Enum values (humanized)
  Draft: 'Borrador',
  New: 'Nuevo',
  'In progress': 'En curso',
  'Pending customer': 'Pendiente del cliente',
  'Ready for qa': 'Listo para QA',
  Testing: 'En pruebas',
  Resolved: 'Resuelto',
  Closed: 'Cerrado',
  Reopened: 'Reabierto',
  'On hold': 'En espera',
  Duplicate: 'Duplicado',
  Cancelled: 'Cancelado',
  Deferred: 'Aplazado',
  Escalated: 'Escalado',
  Critical: 'Crítica',
  High: 'Alta',
  Medium: 'Media',
  Low: 'Baja',
  Blocker: 'Bloqueante',
  Major: 'Mayor',
  Minor: 'Menor',
  Cosmetic: 'Estética',
  Customer: 'Cliente',
  'Customer admin': 'Administrador del cliente',
  'Support agent': 'Agente de soporte',
  Developer: 'Desarrollador',
  'QA engineer': 'Ingeniero de QA',
  'Team lead': 'Jefe de equipo',
  'System admin': 'Administrador del sistema',
  Standard: 'Estándar',
  Premium: 'Premium',
  Enterprise: 'Empresarial',
  Immediate: 'Inmediato',
  Daily: 'Diario',
  Weekly: 'Semanal',
  Monthly: 'Mensual',
  Off: 'Desactivado',
  Pending: 'Pendiente',
  Approved: 'Aprobada',
  Processing: 'En proceso',
  Completed: 'Completada',
  Rejected: 'Rechazada',
  Failed: 'Fallida',
  Sent: 'Enviado',
  Skipped: 'Omitido',
  Paused: 'En pausa',
  Unassigned: 'Sin asignar',
  Severity: 'Severidad',
  Priority: 'Prioridad',
};
